/** KPI ids used in the dashboard (Leading Indicators cards). */
export const PLH_KPI_IDS = [
  "activation-rate",
  "time-to-activation",
  "total-scheduled-hours",
  "total-new-users",
] as const;

/** Card info-tooltips for Leading Indicators; pick by dashboard granularity (week vs month). */
export const PLH_LEADING_KPI_CARD_TOOLTIPS: Record<
  "week" | "month",
  Record<(typeof PLH_KPI_IDS)[number], string>
> = {
  week: {
    "activation-rate":
      "Share of ICP signups in the selected week that reach the 'Schedule Published' step (activated accounts).",
    "time-to-activation":
      "Median time in minutes from signup to activation (schedule published) for the selected week.",
    "total-scheduled-hours":
      "Sum of scheduled hours across all teams that signed up in the selected week. Matches the 'this week's signups only' cohort definition.",
    "total-new-users":
      "Total number of team members (not just owners) across all teams that signed up in the selected week.",
  },
  month: {
    "activation-rate":
      "Share of ICP signups in the selected calendar month that reach the 'Schedule Published' step (activated accounts). Counts follow the monthly funnel (sums of weekly signup-cohort rows; rates recomputed from aggregates).",
    "time-to-activation":
      "Median time in minutes from signup to activation (schedule published) for signups in the selected calendar month.",
    "total-scheduled-hours":
      "Sum of scheduled hours across teams in the selected calendar month's signup cohort (aggregated from weekly rows; same monthly cohort logic as the funnel).",
    "total-new-users":
      "Total number of team members (not just owners) across all teams that signed up in the selected calendar month.",
  },
};

export const PLH_TILE_INFO = {
  activationRate: PLH_LEADING_KPI_CARD_TOOLTIPS.week["activation-rate"],
  timeToActivation: PLH_LEADING_KPI_CARD_TOOLTIPS.week["time-to-activation"],
  activatedToPaid:
    "Percentage of activated accounts in the selected period that convert to a paid plan.",
  weeklyActiveTeams:
    "Weekly active teams: activated teams that published at least one event or created an autofill during the week.",
  newActivatedAccounts:
    "Count of accounts that became activated (schedule published) for the first time in the selected week.",
  funnelTrend:
    "Trend of funnel steps (visits, signups, setup, activated, paid). Weekly view uses ISO weeks. Monthly view uses calendar months; step counts are summed from weekly cohort rows and paid counts follow the same logic—rates are recomputed from those aggregates, not averaged from weekly percentages. Visits use the right axis; other series use the left. Unique visits in monthly mode are the sum of weekly session-uniques (not deduplicated across weeks).",
  totalScheduledHours: PLH_LEADING_KPI_CARD_TOOLTIPS.week["total-scheduled-hours"],
  totalNewUsers: PLH_LEADING_KPI_CARD_TOOLTIPS.week["total-new-users"],
} as const;

/** Long-form "What this means" text for each KPI, shown in the side panel. */
export const PLH_KPI_DETAIL: Record<(typeof PLH_KPI_IDS)[number], string> = {
  "activation-rate":
    "Activation rate = paid conversions / signups. Currently zero because no one has converted to paid yet. This is your north star — every other metric feeds into it.",
  "time-to-activation":
    "Median time from signup → activation, for teams that meet the activation criteria (≥8 users, ≥320 scheduled hours across ≥5 days, with meaningful future hours planned). If you see the value rendered as infinity (∞) here, none of the teams in the selected week reached activation yet.",
  "total-scheduled-hours":
    "Sum of all scheduled hours across this week's signups only. Growing steadily when positive. Shows real product engagement beyond vanity signup numbers.",
  "total-new-users":
    "All team members across all signups, not just account owners. ICP teams pull the average up significantly. Non-ICP signups are mostly solo.",
};

/** Same as PLH_KPI_DETAIL when the dashboard is in monthly granularity. */
export const PLH_KPI_DETAIL_MONTH: Record<(typeof PLH_KPI_IDS)[number], string> = {
  "activation-rate": PLH_KPI_DETAIL["activation-rate"],
  "time-to-activation":
    "Median time from signup → activation, for teams that meet the activation criteria (≥8 users, ≥320 scheduled hours across ≥5 days, with meaningful future hours planned). If you see the value rendered as infinity (∞) here, none of the teams in the selected calendar month's cohort reached activation yet.",
  "total-scheduled-hours":
    "Sum of all scheduled hours across the selected calendar month's signup cohort only. Growing steadily when positive. Shows real product engagement beyond vanity signup numbers.",
  "total-new-users":
    "All team members across that month's signups, not just account owners. ICP teams pull the average up significantly. Non-ICP signups are mostly solo.",
};

/** "Recommended actions" text for each KPI, shown in the side panel. */
export const PLH_KPI_ACTIONS: Record<(typeof PLH_KPI_IDS)[number], string> = {
  "activation-rate":
    "Fix the signup→setup bleed first (76% drop). Then instrument TTFS. Once setup rate improves, activation should follow within 2-3 weeks.",
  "time-to-activation":
    "Add signup_timestamp and first_schedule_saved_timestamp to your event pipeline. Goal: <30 min for ICP users. Non-ICP will likely be slower.",
  "total-scheduled-hours":
    "Keep pushing \"Schedule Created\" users to add more hours. Track WoW change to see if the product is getting stickier for engaged users.",
  "total-new-users":
    "Focus outreach on multi-user teams. Single-user signups rarely convert. Consider adding \"How many people on your team?\" to signup to qualify leads earlier.",
};

/** Funnel step keys used in Activation Funnel (must match ActivationFunnelTile FUNNEL_STEPS). */
export const PLH_FUNNEL_STEP_KEYS = [
  "webVisits",
  "signups",
  "accountSetup",
  "onboarding",
  "activated",
  "scheduleGenerated",
  "schedulePublished",
  "paid",
] as const;

/** Toggle visibility of dashboard sections. Set to false to hide a section. */
export const PLH_SECTION_VISIBILITY: Record<string, boolean> = {
  "ai-brief": false,
  "diagnosis": false,
  "kpi-grid": true,
  "activation-funnel": true,
  "funnel-trend": true,
  "missions": false,
  "icp-analysis": false,
  "new-signups": true,
  "activation-variables": true,
  "materialization-status": false,
};

/** Toggle visibility of individual KPI cards. Set to false to hide a card. */
export const PLH_KPI_VISIBILITY: Record<string, boolean> = {
  "activation-rate": true,
  "time-to-activation": true,
  "total-scheduled-hours": true,
  "total-new-users": true,
};

/** Toggle visibility of UI elements. Set to false to hide. */
export const PLH_UI_VISIBILITY: Record<string, boolean> = {
  "ai-coach-button": false,
  "firecrawl-enrichment": false,
};

/** Toggle visibility of admin tabs. Set to false to hide a tab. */
export const ADMIN_TAB_VISIBILITY: Record<string, boolean> = {
  "support": false,
  "revenue-pace": false,
  "product-led-health": true,
};

/** Toggle visibility of inline AI chat blocks in side panels. Set to false to hide. */
export const PLH_INLINE_CHAT_VISIBILITY: Record<string, boolean> = {
  "icp-editor": false,
  "signup-profile": false,
  "mission-detail": false,
  "funnel-step": false,
  "kpi-detail": false,
  "user-detail": false,
};

/** Description for each funnel step, shown in the step detail panel. Single source for read and import. */
export const PLH_FUNNEL_STEP_DESCRIPTION: Record<(typeof PLH_FUNNEL_STEP_KEYS)[number], string> = {
  webVisits:
    "Unique web visits (top of funnel). Count of distinct visitors in the selected week; baseline for conversion rates.",
  signups:
    "Signups: entering /create flow, entering their email",
  accountSetup:
    "Account setup complete: Verified users (owners), starting the team setup process, etc, the \
    process is finished if users enters their main challenge . Share of signups who finished initial setup.\
    Why: if the owner does not finish the setup process, the team will not be able to use the product.",
  onboarding:
    "First event created: user began the onboarding flow, the onboarding entails the user to create \
    their first schedule (event). Share of account-setup users who started onboarding.\
    Why: the user can now create the artifacts (board, events, etc) that will be used to schedule their work.",
  activated:
    "Activated: reached the activation criteria (Assigned People ≥ 8, Scheduled Hours ≥ 320, \
    Scheduled Days ≥ 5, Future Hours Ratio ≥ 80%). Share of onboarding users who activated.\
    Why: here the user has proven that they can use the product to schedule their work.",
  scheduleGenerated:
    "Schedule generated: user created at least one schedule. Intermediate step before schedule published.",
  schedulePublished:
    "Schedule published (north star): user published a schedule. Primary activation signal; target is a percentage of onboarding.",
  paid:
    "Paid customer: activated users who converted to a paid plan. Conversion rate from activated \
     to paid.\
     Why: the users sees value in the product and is willing to pay for it.",
};
